import { formatUnits } from 'ethers'
import { TFunction } from 'i18next'

import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import { getDateLocale } from '@common/utils/dateLocale'

/** Raw token units → display string (amounts are always formatted en-US). */
export const formatDcaAmount = (raw: string | bigint | null | undefined, decimals: number) => {
  if (raw === null || raw === undefined) return '-'
  try {
    return formatDecimals(Number(formatUnits(BigInt(raw), decimals)), 'amount')
  } catch {
    return '-'
  }
}

/**
 * Raw token units → every significant digit, en-US grouped ("9,999.999999").
 * For the amounts the order is built from (total, per execution, remainder):
 * `formatDcaAmount` rounds 9.999999 down to "9.99" and 0.000001 to "<0.00001",
 * which reads as money lost when 10 is split into 3 executions (Trello #579).
 */
export const formatDcaAmountExact = (
  raw: string | bigint | null | undefined,
  decimals: number
) => {
  if (raw === null || raw === undefined) return '-'
  try {
    const [int = '0', frac = ''] = formatUnits(BigInt(raw), decimals).split('.')
    const trimmed = frac.replace(/0+$/, '')
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return trimmed ? `${grouped}.${trimmed}` : grouped
  } catch {
    return '-'
  }
}

export const formatDcaUsd = (value: number | null | undefined) =>
  value === null || value === undefined || !Number.isFinite(value)
    ? null
    : formatDecimals(value, 'value')

/** "Oct 24, 2026" in the app's date locale — month as a word, so day/month order can't be misread. */
export const formatDcaDate = (date: Date) =>
  date.toLocaleDateString(getDateLocale(), { year: 'numeric', month: 'short', day: 'numeric' })

/** "11:37 PM" / "23:37", the app locale's clock. */
export const formatDcaTime = (date: Date) =>
  date.toLocaleTimeString(getDateLocale(), { hour: '2-digit', minute: '2-digit' })

/**
 * "Oct 24, 2026 · 11:37 PM" in the app's date locale. The Review popup, Activity
 * and the schedule form all go through formatDcaDate / formatDcaTime, so a
 * date reads the same everywhere (Trello #580). The form can't rely on the
 * browser's own `<input type="date|time">` text for that: the browser draws it
 * in the OS region's format (dd/mm, 24h on a Vietnamese Mac), which the page
 * can neither read nor change — see DcaScheduleInputs.
 */
export const formatDcaDateTime = (iso: string | null | undefined) => {
  if (!iso) return '-'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '-'
  return `${formatDcaDate(date)} · ${formatDcaTime(date)}`
}

/** Activity → DCA; same format as {@link formatDcaDateTime}. */
export const formatDcaDateTimeShort = formatDcaDateTime

/** Seconds → "1 day", "30 minutes", in the largest unit that divides evenly. */
export const formatDcaInterval = (seconds: number, t: TFunction) => {
  if (seconds > 0 && seconds % 604800 === 0)
    return t('{{count}} weeks', { count: seconds / 604800 })
  if (seconds > 0 && seconds % 86400 === 0) return t('{{count}} days', { count: seconds / 86400 })
  if (seconds > 0 && seconds % 3600 === 0) return t('{{count}} hours', { count: seconds / 3600 })
  return t('{{count}} minutes', { count: Math.round(seconds / 60) })
}

/**
 * A typed amount with en-US thousand groups ("8000000000.5" → "8,000,000,000.5").
 * Regex, not Number().toLocaleString: big integers would lose digits. NumberInput
 * strips the commas again before the value reaches onChangeText.
 */
export const groupDcaAmountInput = (value: string) => {
  if (!value) return value
  const [int = '', ...rest] = value.split('.')
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return rest.length ? `${grouped}.${rest.join('')}` : grouped
}

const pad = (n: number) => String(n).padStart(2, '0')

/** `<input type="date">` value ("2026-10-24") → local midnight, or null. */
export const fromDateInputValue = (value: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

/** `<input type="time">` value ("23:37") → that time today, or null. */
export const fromTimeInputValue = (value: string) => {
  const m = /^(\d{2}):(\d{2})/.exec(value)
  if (!m) return null
  const date = new Date()
  date.setHours(Number(m[1]), Number(m[2]), 0, 0)
  return date
}

/** Local date as `<input type="date">` wants it. */
export const toDateInputValue = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

/** Local time as `<input type="time">` wants it. */
export const toTimeInputValue = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`
